import { useState, useRef } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowLeft, Edit, Send, Printer, Download, Euro, CreditCard,
  CheckCircle2, XCircle, Clock, Building2, Calendar, FileText, AlertCircle,
  Eye, Mail, Loader2
} from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { useReactToPrint } from "react-to-print";
import { InvoicePDF } from "@/components/Billing/InvoicePDF";
import { DeliveryNoteDialog } from "@/components/DeliveryNote/DeliveryNoteDialog";
import { FileText } from "lucide-react";
import type { Invoice, InvoicePayment, BillingSettings } from "@/types/billing";

const statusColors: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  issued: "bg-blue-100 text-blue-700",
  sent: "bg-indigo-100 text-indigo-700",
  paid: "bg-green-100 text-green-700",
  partial: "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-700",
  rectified: "bg-purple-100 text-purple-700",
};

const statusLabels: Record<string, string> = {
  draft: "Borrador",
  issued: "Emitida",
  sent: "Enviada",
  paid: "Cobrada",
  partial: "Parcial",
  cancelled: "Anulada",
  rectified: "Rectificada",
};

export default function InvoiceDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const printRef = useRef<HTMLDivElement>(null);

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [deliveryNoteOpen, setDeliveryNoteOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const [paymentForm, setPaymentForm] = useState({
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    amount: 0,
    payment_method: 'transfer',
    reference: '',
  });

  const [emailForm, setEmailForm] = useState({
    to: '',
    subject: '',
    body: '',
  });

  // Fetch invoice
  const { data: invoice, isLoading } = useQuery<Invoice>({
    queryKey: ["invoice-detail", id],
    queryFn: async () => {
      const { data, error } = await api
        .from("invoices")
        .select(`
          *,
          customer:billing_customers(*),
          lines:invoice_lines(*),
          payments:invoice_payments(*)
        `)
        .eq("id", id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Fetch billing settings for PDF
  const { data: billingSettings } = useQuery<BillingSettings | null>({
    queryKey: ["billing-settings"],
    queryFn: async () => {
      const { data, error } = await api
        .from("billing_settings")
        .select("*")
        .limit(1)
        .maybeSingleRow();
      if (error) throw error;
      return data;
    },
  });

  // Print handler
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Factura-${invoice?.invoice_number || 'borrador'}`,
  });

  // Open email dialog with prefilled data
  const openEmailDialog = () => {
    if (!invoice) return;
    const customer = invoice.customer as any;
    setEmailForm({
      to: customer?.email || '',
      subject: `Factura ${invoice.invoice_number || 'Borrador'} - ${billingSettings?.company_name || 'Athenea'}`,
      body: `Estimado/a ${customer?.contact_name || customer?.legal_name || 'cliente'},

Adjunto encontrará la factura ${invoice.invoice_number || ''} por importe de ${formatCurrency(invoice.total_amount)}.

Fecha de emisión: ${format(new Date(invoice.issue_date), "dd/MM/yyyy")}
${invoice.due_date ? `Fecha de vencimiento: ${format(new Date(invoice.due_date), "dd/MM/yyyy")}` : ''}

Quedamos a su disposición para cualquier consulta.

Atentamente,
${billingSettings?.company_name || 'Athenea'}`,
    });
    setEmailDialogOpen(true);
  };

  // Email mutation (placeholder - actual email sending would need backend)
  const emailMutation = useMutation({
    mutationFn: async () => {
      if (!emailForm.to) throw new Error("Introduce un email de destino");

      // For now, we'll mark the invoice as sent and show the mailto link
      // In production, this would call a backend endpoint to send the email with PDF
      if (invoice?.status === 'issued') {
        await api
          .from("invoices")
          .update({ status: 'sent', updated_at: new Date().toISOString() })
          .eq("id", id);
      }

      // Open mailto as fallback (real implementation would send via API)
      const mailtoLink = `mailto:${emailForm.to}?subject=${encodeURIComponent(emailForm.subject)}&body=${encodeURIComponent(emailForm.body)}`;
      window.open(mailtoLink, '_blank');

      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice-detail", id] });
      setEmailDialogOpen(false);
      toast.success("Email preparado. Se ha abierto tu cliente de correo.");
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Mark as sent
  const sendMutation = useMutation({
    mutationFn: async () => {
      const { error } = await api
        .from("invoices")
        .update({ status: 'sent', updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice-detail", id] });
      toast.success("Factura marcada como enviada");
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Register payment
  const paymentMutation = useMutation({
    mutationFn: async () => {
      if (!paymentForm.amount || paymentForm.amount <= 0) {
        throw new Error("Introduce un importe válido");
      }
      const { error } = await api.from("invoice_payments").insert({
        invoice_id: id,
        payment_date: paymentForm.payment_date,
        amount: paymentForm.amount,
        payment_method: paymentForm.payment_method,
        reference: paymentForm.reference || null,
        created_by: user?.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice-detail", id] });
      setPaymentDialogOpen(false);
      setPaymentForm({
        payment_date: format(new Date(), 'yyyy-MM-dd'),
        amount: 0,
        payment_method: 'transfer',
        reference: '',
      });
      toast.success("Cobro registrado");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Cargando factura...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!invoice) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Factura no encontrada</p>
          <Button asChild className="mt-4">
            <Link to="/billing/invoices">Volver a facturas</Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const pendingAmount = invoice.total_amount - invoice.paid_amount;
  const payments = (invoice.payments || []) as InvoicePayment[];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/billing/invoices"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold font-mono">
                  {invoice.invoice_number || 'Borrador'}
                </h1>
                <Badge className={statusColors[invoice.status]}>
                  {statusLabels[invoice.status]}
                </Badge>
                {invoice.invoice_type === 'rectificativa' && (
                  <Badge variant="outline">Rectificativa</Badge>
                )}
                {invoice.invoice_type === 'proforma' && (
                  <Badge variant="outline">Proforma</Badge>
                )}
              </div>
              <p className="text-muted-foreground">
                {format(new Date(invoice.issue_date), "dd 'de' MMMM 'de' yyyy", { locale: es })}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {invoice.status === 'draft' && (
              <Button variant="outline" asChild>
                <Link to={`/billing/invoices/${id}/edit`}>
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Link>
              </Button>
            )}
            {invoice.status === 'issued' && (
              <Button variant="outline" onClick={() => sendMutation.mutate()}>
                <Send className="w-4 h-4 mr-2" />
                Marcar enviada
              </Button>
            )}
            {['issued', 'sent', 'partial'].includes(invoice.status) && (
              <Button onClick={() => {
                setPaymentForm({ ...paymentForm, amount: pendingAmount });
                setPaymentDialogOpen(true);
              }}>
                <Euro className="w-4 h-4 mr-2" />
                Registrar cobro
              </Button>
            )}
            <Button variant="outline" onClick={() => setPreviewDialogOpen(true)}>
              <Eye className="w-4 h-4 mr-2" />
              Vista previa
            </Button>
            <Button variant="outline" onClick={openEmailDialog}>
              <Mail className="w-4 h-4 mr-2" />
              Enviar email
            </Button>
            <Button variant="outline" onClick={() => handlePrint()}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
            <Button variant="outline" onClick={() => setDeliveryNoteOpen(true)}>
              <FileText className="w-4 h-4 mr-2" />
              Generar Albaran
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Datos del cliente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="font-medium text-lg">{invoice.customer_name}</p>
                    <p className="font-mono text-sm text-muted-foreground">{invoice.customer_tax_id}</p>
                    {invoice.customer_address && (
                      <p className="text-sm mt-2 whitespace-pre-line text-muted-foreground">
                        {invoice.customer_address}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Forma de pago:</span>
                      <span className="capitalize">{invoice.payment_method || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Vencimiento:</span>
                      <span>
                        {invoice.due_date
                          ? format(new Date(invoice.due_date), "dd/MM/yyyy")
                          : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Invoice Lines */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Detalle
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Descripción</TableHead>
                      <TableHead className="text-right">Cantidad</TableHead>
                      <TableHead className="text-right">Precio</TableHead>
                      <TableHead className="text-right">Dto.</TableHead>
                      <TableHead className="text-right">IVA</TableHead>
                      <TableHead className="text-right">Importe</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(invoice.lines || []).map((line: any) => (
                      <TableRow key={line.id}>
                        <TableCell>{line.description}</TableCell>
                        <TableCell className="text-right">{line.quantity}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(line.unit_price)}</TableCell>
                        <TableCell className="text-right">
                          {line.discount_percent > 0 ? `${line.discount_percent}%` : '-'}
                        </TableCell>
                        <TableCell className="text-right">{line.tax_rate}%</TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {formatCurrency(line.subtotal)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Payments History */}
            {payments.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Historial de cobros
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Método</TableHead>
                        <TableHead>Referencia</TableHead>
                        <TableHead className="text-right">Importe</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>{format(new Date(payment.payment_date), "dd/MM/yyyy")}</TableCell>
                          <TableCell className="capitalize">{payment.payment_method}</TableCell>
                          <TableCell>{payment.reference || '-'}</TableCell>
                          <TableCell className="text-right font-mono text-green-600">
                            +{formatCurrency(payment.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {invoice.notes && (
              <Card>
                <CardHeader>
                  <CardTitle>Observaciones</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-line">{invoice.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Totals */}
            <Card>
              <CardHeader>
                <CardTitle>Importes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Base imponible</span>
                  <span className="font-mono">{formatCurrency(invoice.subtotal)}</span>
                </div>

                {(invoice.tax_breakdown || []).map((tax: any, idx: number) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">IVA {tax.tax_rate}%</span>
                    <span className="font-mono">{formatCurrency(tax.tax_amount)}</span>
                  </div>
                ))}

                <Separator />

                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="font-mono">{formatCurrency(invoice.total_amount)}</span>
                </div>

                {invoice.paid_amount > 0 && (
                  <>
                    <div className="flex justify-between text-green-600">
                      <span>Cobrado</span>
                      <span className="font-mono">-{formatCurrency(invoice.paid_amount)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-lg font-bold">
                      <span>Pendiente</span>
                      <span className={`font-mono ${pendingAmount > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        {formatCurrency(pendingAmount)}
                      </span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* VeriFactu Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  VeriFactu
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invoice.verifactu_status === 'accepted' ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="w-5 h-5" />
                    <span>Aceptada por AEAT</span>
                  </div>
                ) : invoice.verifactu_status === 'rejected' ? (
                  <div className="flex items-center gap-2 text-red-600">
                    <XCircle className="w-5 h-5" />
                    <span>Rechazada</span>
                  </div>
                ) : invoice.verifactu_status === 'submitted' ? (
                  <div className="flex items-center gap-2 text-blue-600">
                    <Clock className="w-5 h-5" />
                    <span>Enviada, pendiente respuesta</span>
                  </div>
                ) : invoice.status !== 'draft' ? (
                  <div className="text-muted-foreground text-sm">
                    <p>Pendiente de envío a AEAT</p>
                    <Button variant="outline" size="sm" className="mt-2" disabled>
                      Enviar a VeriFactu
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Se enviará automáticamente al emitir la factura
                  </p>
                )}

                {invoice.verifactu_hash && (
                  <div className="mt-3 p-2 bg-muted rounded text-xs font-mono break-all">
                    Hash: {invoice.verifactu_hash.substring(0, 32)}...
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Información</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Creada:</span>
                  <span>{format(new Date(invoice.created_at), "dd/MM/yyyy HH:mm")}</span>
                </div>
                {invoice.updated_at !== invoice.created_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Modificada:</span>
                    <span>{format(new Date(invoice.updated_at), "dd/MM/yyyy HH:mm")}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar cobro</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Pendiente de cobro:</p>
              <p className="text-xl font-bold">{formatCurrency(pendingAmount)}</p>
            </div>

            <div className="space-y-2">
              <Label>Fecha de cobro</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Importe</Label>
              <Input
                type="number"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })}
                step={0.01}
                min={0}
              />
            </div>

            <div className="space-y-2">
              <Label>Método de pago</Label>
              <Select
                value={paymentForm.payment_method}
                onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transfer">Transferencia</SelectItem>
                  <SelectItem value="cash">Efectivo</SelectItem>
                  <SelectItem value="card">Tarjeta</SelectItem>
                  <SelectItem value="direct_debit">Domiciliación</SelectItem>
                  <SelectItem value="check">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Referencia (opcional)</Label>
              <Input
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                placeholder="Número de transferencia, cheque, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={() => paymentMutation.mutate()} disabled={paymentMutation.isPending}>
              {paymentMutation.isPending ? "Guardando..." : "Registrar cobro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vista previa de factura</DialogTitle>
            <DialogDescription>
              Factura {invoice.invoice_number || 'Borrador'}
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg overflow-hidden bg-white">
            <InvoicePDF
              invoice={invoice}
              settings={billingSettings || undefined}
            />
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Cerrar
            </Button>
            <Button onClick={() => handlePrint()}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
            <Button onClick={() => {
              setPreviewDialogOpen(false);
              openEmailDialog();
            }}>
              <Mail className="w-4 h-4 mr-2" />
              Enviar por email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Enviar factura por email</DialogTitle>
            <DialogDescription>
              Se abrirá tu cliente de correo con la información preparada
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Para</Label>
              <Input
                type="email"
                value={emailForm.to}
                onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                placeholder="email@cliente.com"
              />
            </div>

            <div className="space-y-2">
              <Label>Asunto</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Mensaje</Label>
              <Textarea
                value={emailForm.body}
                onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                rows={8}
              />
            </div>

            <div className="p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
              <p className="font-medium">Nota:</p>
              <p>
                Recuerda adjuntar el PDF de la factura manualmente.
                Puedes generarlo usando el botón "Imprimir" → "Guardar como PDF".
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={() => emailMutation.mutate()} disabled={emailMutation.isPending}>
              {emailMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Preparando...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Abrir cliente de correo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden PDF for printing */}
      <div className="hidden">
        <InvoicePDF
          ref={printRef}
          invoice={invoice}
          settings={billingSettings || undefined}
        />
      </div>

      {/* Delivery Note Dialog */}
      <DeliveryNoteDialog
        open={deliveryNoteOpen}
        onOpenChange={setDeliveryNoteOpen}
        initialCustomer={{
          name: invoice.customer_name || '',
          cif: invoice.customer_tax_id || '',
          address: invoice.customer_address || '',
        }}
        initialItems={invoice.lines?.map((line: any) => ({
          article: line.description?.substring(0, 50) || '',
          description: line.description || '',
          quantity: line.quantity || 1,
          unit: line.unit || 'ud',
          unitPrice: line.unit_price || 0,
          discount: line.discount_percent || 0,
        }))}
      />
    </DashboardLayout>
  );
}
